// utils/rankingDraft.js
//
// The in-progress Custom Ranking on web. The rules (Top 5 to Top 20, no
// duplicate titles, exactly `size` to save) live in @aluvo/constants so web
// and mobile behave the same; this file adds localStorage persistence so a
// refresh, a network error or the sign-up detour doesn't lose the work.
// It is a convenience, not storage: one device, one draft, 7-day expiry.
import {
    RANKING_DRAFT_STORAGE_KEY,
    emptyRankingDraft,
    newRankingDraftId,
    normalizeRankingCardOptions,
    normalizeStoredRankingDraft,
} from '@aluvo/constants/src/rankings';

const defaultStorage = () => (typeof window !== 'undefined' ? window.localStorage : null);

/** A random UUID v4 for the draft; sent as clientDraftId so saving is idempotent. */
export const newDraftId = newRankingDraftId;

export function freshRankingDraft(options = {}) {
    return emptyRankingDraft({ draftId: newDraftId(), ...options });
}

export function loadRankingDraft(storage = defaultStorage()) {
    try {
        const draft = normalizeStoredRankingDraft(JSON.parse(storage?.getItem(RANKING_DRAFT_STORAGE_KEY) || 'null'));
        if (draft && !draft.draftId) draft.draftId = newDraftId();
        return draft;
    } catch {
        return null;
    }
}

export function saveRankingDraft(draft, storage = defaultStorage()) {
    try {
        storage?.setItem(RANKING_DRAFT_STORAGE_KEY, JSON.stringify({ ...draft, updatedAt: new Date().toISOString() }));
    } catch { /* storage full or blocked: the editor still works */ }
}

export function clearRankingDraft(storage = defaultStorage()) {
    try { storage?.removeItem(RANKING_DRAFT_STORAGE_KEY); } catch { /* ignore */ }
}

/** Worth keeping on the device: anything the user typed or picked. */
export const hasDraftContent = (draft) => Boolean(draft && (draft.items.length || draft.title.trim()));

// ── Share-image choices ──────────────────────────────────────────────────────
// Remembered per ranking on this device, like the mobile share-image builder
// remembers them per list (@aluvo/list_card_options/<id>). Presentation only:
// they never change the ranking or what other people's images look like.
const cardOptionsKey = (id) => `aluvo_ranking_card_options/${id}`;

export function loadRankingCardOptions(id, storage = defaultStorage()) {
    try {
        return normalizeRankingCardOptions(JSON.parse(storage?.getItem(cardOptionsKey(id)) || 'null'));
    } catch {
        return normalizeRankingCardOptions(null);
    }
}

export function saveRankingCardOptions(id, options, storage = defaultStorage()) {
    try { storage?.setItem(cardOptionsKey(id), JSON.stringify(options)); } catch { /* sharing still works without it */ }
}
